import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Memory,
  Queue,
  Refresh,
  RestartAlt,
  Speed,
  Storage,
  Warning,
} from '@mui/icons-material';

import { getOperationsOverview, runOperationsControl } from '../services/adminApi';
import { OperationsLogsView, SpeechCallsView } from './OperationsHistory';

const REFRESH_MS = 30000;

function displayDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
}

function displayNumber(value, suffix = '') {
  return Number.isFinite(value) ? `${value}${suffix}` : 'Not available';
}

function displayMoney(value) {
  if (value === null || value === undefined || value === '') return 'Not available';
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : 'Not available';
}

function displayGigabytes(value) {
  return Number.isFinite(value) ? `${(value / 1_000_000_000).toFixed(2)} GB` : 'Not available';
}

function statusColor(status) {
  if (['allowed', 'healthy', 'ready'].includes(status)) return 'success';
  if (['blocked', 'failed', 'unavailable'].includes(status)) return 'error';
  if (['busy', 'stale'].includes(status)) return 'warning';
  return 'default';
}

function StatusChip({ label, status }) {
  const normalized = status || 'unknown';
  return (
    <Chip
      color={statusColor(normalized)}
      label={label || normalized.replaceAll('_', ' ')}
      size="small"
      sx={{ textTransform: 'capitalize' }}
    />
  );
}

function MetricCard({ icon, label, value }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography color="text.secondary" variant="body2">{label}</Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }} variant="h5">{value}</Typography>
          </Box>
          <Box color="primary.main" display="flex">{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function Detail({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography color="text.secondary" variant="caption">{label}</Typography>
      <Typography component="div" sx={{ overflowWrap: 'anywhere' }} variant="body2">
        {value}
      </Typography>
    </Box>
  );
}

function WorkerDetails({ worker }) {
  const telemetry = worker.telemetry;
  const state = telemetry?.worker || {};
  const gpu = telemetry?.gpu || {};
  const power = telemetry?.power || {};
  const disk = telemetry?.disk || {};
  const gpuIsActive = state.activeWork === true;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Laptop Worker</Typography>
        <StatusChip status={worker.status} />
      </Stack>
      {!gpuIsActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          GPU is not sampled while idle. The GPU values below are from the last active sample.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Detail
            label={gpuIsActive ? 'Latest active GPU sample' : 'Last active GPU sample'}
            value={displayDate(gpu.sampledAt)}
          />
        </Grid>
        <Grid item xs={6} md={3}><Detail label="GPU clock" value={displayNumber(gpu.clockMHz, ' MHz')} /></Grid>
        <Grid item xs={6} md={3}><Detail label="GPU use" value={displayNumber(gpu.utilizationPercent, '%')} /></Grid>
        <Grid item xs={6} md={3}><Detail label="VRAM" value={displayNumber(gpu.vramUsedMiB, ' MiB')} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Temperature" value={displayNumber(gpu.temperatureC, ' C')} /></Grid>
        <Grid item xs={6} md={3}><Detail label="GPU power" value={displayNumber(gpu.powerDrawW, ' W')} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Power line" value={power.lineStatus || 'Unknown'} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Last generation" value={displayNumber(state.lastGenerationMs, ' ms')} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Queue latency" value={displayNumber(state.queuePollLatencyMs, ' ms')} /></Grid>
        <Grid item xs={12} md={4}><Detail label="Last success" value={displayDate(state.lastSuccessAt)} /></Grid>
        <Grid item xs={12} md={4}><Detail label="Last failure" value={displayDate(state.lastFailureAt)} /></Grid>
        <Grid item xs={12} md={4}><Detail label="Telemetry received" value={displayDate(telemetry?.receivedAt)} /></Grid>
        <Grid item xs={12} md={6}><Detail label="Disk free" value={displayGigabytes(disk.freeBytes)} /></Grid>
        <Grid item xs={12} md={6}><Detail label="Recent failures" value={displayNumber(state.recentFailures)} /></Grid>
      </Grid>
    </Paper>
  );
}

function ModalDeployment({ deployment }) {
  const billing = deployment.billing || { status: 'unavailable' };
  const health = deployment.health || { status: 'unknown' };
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
        mb={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ overflowWrap: 'anywhere' }} variant="h6">{deployment.app}</Typography>
          <Typography color="text.secondary" variant="body2">
            {deployment.workspace} / {deployment.environment}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {deployment.active && <Chip label="Active" color="primary" size="small" />}
          <StatusChip label={`Modal health: ${health.status || 'unknown'}`} status={health.status} />
        </Stack>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}><Detail label="Profile" value={deployment.profile} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Billing state" value={<StatusChip status={billing.status} />} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Fallback eligibility" value={billing.fallbackAllowed ? 'Allowed by billing' : 'Blocked by billing'} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Reason" value={(billing.reason || 'unknown').replaceAll('_', ' ')} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Metered usage" value={displayMoney(billing.meteredCost)} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Credits applied" value={displayMoney(billing.creditsApplied)} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Spend limit" value={displayMoney(billing.spendLimit)} /></Grid>
        <Grid item xs={6} md={3}><Detail label="Billed cost" value={displayMoney(billing.billedCost)} /></Grid>
        <Grid item xs={12} md={4}><Detail label="Billing checked" value={displayDate(billing.checkedAt)} /></Grid>
        <Grid item xs={12} md={4}><Detail label="Free storage" value={displayMoney(billing.freeStorage)} /></Grid>
        <Grid item xs={12} md={4}><Detail label="Last Modal result" value={displayDate(health.checkedAt)} /></Grid>
      </Grid>
    </Paper>
  );
}

function ControlPanel({ agent, onAction, pending, speech, worker }) {
  const agentReady = agent?.status === 'ready';
  const fallbackEnabled = speech.fallbackProvider === 'modal';
  const workerBusy = Number(worker.activeRequests) > 0;
  const workerDraining = worker.status === 'draining';

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2}>
        <Stack
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography variant="h6">Safe Controls</Typography>
            <Typography color="text.secondary" variant="body2">
              Laptop control agent: {agentReady ? 'ready' : 'unavailable'}
            </Typography>
          </Box>
          <StatusChip status={agent?.status} />
        </Stack>
        {!agentReady && (
          <Alert severity="warning">Laptop controls are unavailable until the control agent reconnects.</Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={1}>
          <Button disabled={pending} onClick={() => onAction('/api/v1/admin/operations/modal/health-check')} variant="outlined">
            Check Modal Health
          </Button>
          <Button disabled={!agentReady || pending} onClick={() => onAction('/api/v1/admin/operations/worker/drain')} variant="outlined">
            Drain Worker
          </Button>
          <Button disabled={!agentReady || pending} onClick={() => onAction('/api/v1/admin/operations/worker/resume')} variant="outlined">
            Resume Worker
          </Button>
          <Button disabled={!agentReady || pending} onClick={() => onAction('/api/v1/admin/operations/worker/start')} variant="outlined">
            Start Worker
          </Button>
          <Button disabled={!agentReady || pending} onClick={() => onAction('/api/v1/admin/operations/worker/restart')} startIcon={<RestartAlt />} variant="outlined">
            Restart Worker
          </Button>
          <Button color="error" disabled={!agentReady || !workerDraining || workerBusy || pending} onClick={() => onAction('/api/v1/admin/operations/worker/stop')} variant="outlined">
            Stop Worker
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1}>
          <Select
            aria-label="Speech provider"
            disabled={pending}
            native
            onChange={(event) => onAction(`/api/v1/admin/operations/speech-provider/${event.target.value}`)}
            value={speech.provider || 'worker'}
          >
            <option value="worker">Laptop worker</option>
            <option value="modal">Modal</option>
          </Select>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={fallbackEnabled}
              disabled={pending}
              inputProps={{ 'aria-label': 'Enable Modal fallback' }}
              onChange={(event) => onAction(`/api/v1/admin/operations/fallback/${event.target.checked ? 'enabled' : 'disabled'}`)}
            />
            <Typography variant="body2">Enable Modal fallback</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function Operations() {
  const [view, setView] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [controlError, setControlError] = useState('');
  const [pendingControl, setPendingControl] = useState(false);

  const load = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const response = await getOperationsOverview();
      setData(response.data);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Operational status is unavailable');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const runControl = useCallback(async (path) => {
    setPendingControl(true);
    setControlError('');
    try {
      await runOperationsControl(path);
      await load();
    } catch (requestError) {
      setControlError(requestError.response?.data?.error || 'Operational control is unavailable');
    } finally {
      setPendingControl(false);
    }
  }, [load]);

  useEffect(() => {
    load({ initial: true });
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (loading) {
    return (
      <Box alignItems="center" display="flex" justifyContent="center" minHeight={320}>
        <CircularProgress aria-label="Loading operations" />
      </Box>
    );
  }

  const worker = data?.worker || { status: 'unavailable', activeRequests: null, queueDepth: null };
  const agent = data?.agent || { status: 'unavailable' };
  const speech = data?.speech || {};
  const deployments = data?.modal?.deployments || [];
  const workerPowerFault = worker.status === 'faulted'
    && worker.telemetry?.worker?.fault?.code === 'WORKER_GPU_POWER_FAULT';
  const workerExpectedRunning = worker.expectedRunning !== false;
  const workerNotExpected = !workerExpectedRunning && worker.status === 'unavailable';
  const degraded = worker.status === 'faulted'
    || (workerExpectedRunning && worker.status === 'unavailable')
    || deployments.some((deployment) => ['blocked', 'stale', 'unavailable'].includes(
      deployment.billing?.status,
    ));
  const summarySeverity = degraded ? 'warning' : workerNotExpected ? 'info' : 'success';
  const summaryMessage = degraded
    ? 'One or more services need attention.'
    : workerNotExpected
      ? 'Laptop worker is not assigned to this environment.'
      : 'Speech services are ready.';

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4">Operations</Typography>
          <Typography color="text.secondary" variant="body2">
            Updated {displayDate(data?.checkedAt)}
          </Typography>
        </Box>
        <Tooltip title="Refresh operational status">
          <span>
            <IconButton
              aria-label="Refresh operational status"
              disabled={refreshing}
              onClick={() => load()}
            >
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Tabs
        aria-label="Operations views"
        onChange={(_event, value) => setView(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        value={view}
      >
        <Tab label="Overview" />
        <Tab label="Laptop Calls" />
        <Tab label="Logs" />
      </Tabs>

      {view === 0 ? (
        <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {controlError && <Alert severity="error" sx={{ mb: 2 }}>{controlError}</Alert>}
      {(data?.alerts || []).map((alert) => (
        <Alert key={alert.type} severity="error" sx={{ mb: 2 }}>
          <strong>{alert.title}.</strong> {alert.message}
        </Alert>
      ))}
      {data && (
        <Alert
          icon={degraded ? <Warning /> : <CheckCircle />}
          severity={summarySeverity}
          sx={{ mb: 3 }}
        >
          {summaryMessage}
        </Alert>
      )}
      {workerPowerFault && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Laptop GPU power fault detected. Speech jobs are paused. Reconnect the charger and
          confirm Turbo mode.
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<Speed />} label="Provider" value={speech.provider || 'Unknown'} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<Memory />} label="Worker capacity" value={displayNumber(speech.workerCapacity)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<Storage />} label="Active requests" value={displayNumber(worker.activeRequests)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<Queue />} label="Queued jobs" value={displayNumber(worker.queueDepth)} />
        </Grid>
      </Grid>

      <Stack spacing={3}>
        <ControlPanel
          agent={agent}
          onAction={runControl}
          pending={pendingControl}
          speech={speech}
          worker={worker}
        />
        {worker.status === 'unavailable' && workerExpectedRunning ? (
          <Alert icon={<ErrorIcon />} severity="error">Laptop worker telemetry is unavailable.</Alert>
        ) : worker.status !== 'unavailable' ? (
          <WorkerDetails worker={worker} />
        ) : null}

        <Box>
          <Typography mb={1.5} variant="h5">Modal Deployments</Typography>
          {data?.modal?.dataStatus === 'unavailable' ? (
            <Alert severity="error">Modal operational data is unavailable.</Alert>
          ) : deployments.length === 0 ? (
            <Alert severity="info">No Modal deployment is configured.</Alert>
          ) : (
            <Stack spacing={2}>
              {deployments.map((deployment) => (
                <ModalDeployment key={deployment.id} deployment={deployment} />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
        </>
      ) : view === 1 ? (
        <SpeechCallsView />
      ) : (
        <OperationsLogsView />
      )}
    </Box>
  );
}
